import { Drawer } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { FieldArray, Formik } from "formik";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ATTACHMENT_TYPES,
  RELATIONSHIP,
  SUPPORTING_INFORMATION_CATEGORIES,
} from "utils/constants";
import { downloadDocument } from "utils/helpers/files";
import { bindDateInputProps } from "utils/helpers/input";
import { buildMappedProduct } from "../mappedProduct";
import validation from "./validation";
import ProductCard from "./widgets/ProductCard";

import { FaPlus } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import {
  hundredYearsAgoStart,
  isMajor,
  twentySevenYearsAgoStart,
} from "utils/helpers/date";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import Header from "../Components/Header";
import Layout from "../Components/Layout";
import Swiper from "../Components/Swiper";
import RelatedPerson from "./widgets/RelatedBlock";

const View = ({
  onNext,
  products,
  disabledProducts,
  contact,
  downloadSingleDocument,
  locked,
  onBack,
  selectedProducts,
  setSelectedProducts,
}) => {
  const spouse = get(contact, "relatedPersons", []).find(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );
  const children = get(contact, "relatedPersons", []).filter(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  );
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [drawerGuaranteeVisible, setDrawerGuaranteeVisible] = useState(false);
  const [selectedGuarantee, setSelectedGuarantee] = useState(null);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const allDefined = Object.values(selectedProducts).every(
    (v) => v !== undefined,
  );
  const onSubmit = ({ spouse, children }) => {
    onNext({
      contact: {
        id: contact.id,
        relatedPersons: [spouse, ...children].filter(Boolean),
      },
    });
  };

  const [addBeneficiaries, setAddBeneficiaries] = useState({
    spouse: !isEmpty(spouse),
  });

  return (
    <Formik
      initialValues={{
        spouse,
        children,
      }}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ handleSubmit, values, ...formProps }) => [
        <Layout key={"Layout"}>
          <Header
            title="Vous y êtes ! Voici nos offres spécialement<br />conçues pour vous"
            highlight="nos offres"
            subtitle="Offres"
            onBack={onBack}
          />
          <div className="products__page">
            <h1 className="products__page--title">
              Vous <span>(Adhérent principal)</span>
            </h1>
            <div className="products__wrapper collective">
              <Swiper>
                {products.map((insurancePlan, i) => {
                  const supportingInfo = get(
                    insurancePlan,
                    "supportingInformation",
                    [],
                  ).find(
                    (item) =>
                      item.category ===
                      SUPPORTING_INFORMATION_CATEGORIES.HEADLIGHT_WARRANTY,
                  );
                  return (
                    <ProductCard
                      key={`ProductCard-${i}`}
                      isSelected={
                        selectedProducts[get(contact, "id")]?.id ===
                        get(insurancePlan, "id")
                      }
                      selectProduct={() =>
                        setSelectedProducts((prev) => ({
                          ...prev,
                          [get(contact, "id")]:
                            buildMappedProduct(insurancePlan),
                        }))
                      }
                      id={get(insurancePlan, "id")}
                      name={get(insurancePlan, "name")}
                      alias={get(insurancePlan, "alias")}
                      icon={get(insurancePlan, "icon")}
                      showGuarantee={(guaranteeItem) => {
                        setDrawerGuaranteeVisible(true);
                        setSelectedGuarantee(guaranteeItem);
                      }}
                      disabled={disabledProducts.includes(
                        get(insurancePlan, "id"),
                      )}
                      info={{
                        title: "Les garanties phares",
                        items: Array.isArray(supportingInfo?.value)
                          ? supportingInfo.value
                          : [],
                      }}
                      link={{
                        onClick: () => {
                          downloadSingleDocument({
                            variables: {
                              where: {
                                insurancePlans: { id: insurancePlan?.id },
                                type: ATTACHMENT_TYPES.BROCHURE,
                              },
                            },
                            onCompleted: (_) => {
                              const { document } = omitDeep(
                                _.downloadSingleDocument,
                                "__typename",
                              );
                              downloadDocument(document);
                            },
                          });
                        },
                        label: TRANSLATION.PRODUCTS.COVERAGE_DETAIL,
                      }}
                      recommendation={{
                        label: "Recommandé",
                        value: i === 1,
                      }}
                    />
                  );
                })}
              </Swiper>
              <div className="swiper__arrows">
                <div className="swiper-button-prev"></div>
                <div className="swiper-button-next"></div>
              </div>
            </div>
            <div className="relatedPersons__area collective">
              {addBeneficiaries.spouse ? (
                <RelatedPerson
                  products={products}
                  title={"Conjoint(e)"}
                  contact={get(values, "spouse.identity")}
                  TRANSLATION={TRANSLATION}
                  selectedProducts={selectedProducts}
                  setSelectedProducts={setSelectedProducts}
                  defaultPickerValue={
                    !get(values, `spouse.identity.user.birthDate`) &&
                    dayjs().subtract(24, "years")
                  }
                  disabledDate={(current) =>
                    current > isMajor || current < hundredYearsAgoStart
                  }
                  onDelete={() => {
                    const key = get(values, "spouse.identity.id");
                    setSelectedProducts((prev) => {
                      const updated = { ...prev };
                      delete updated[key];
                      return updated;
                    });
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      spouse: false,
                    }));
                    formProps.setFieldValue("spouse", undefined);
                  }}
                  inputProps={{
                    ...bindDateInputProps({
                      name: "spouse.identity.user.birthDate",
                      values,
                      ...formProps,
                    }),
                  }}
                />
              ) : (
                <Button
                  type={"primary-light"}
                  className="children__count__button"
                  onClick={() => {
                    const id = cuid();
                    setSelectedProducts((prev) => ({
                      ...prev,
                      [id]: undefined,
                    }));
                    setAddBeneficiaries((prev) => ({
                      ...prev,
                      spouse: true,
                    }));
                    formProps.setFieldValue("spouse", {
                      id: cuid(),
                      relationship: RELATIONSHIP.SPOUSE,
                      identity: {
                        id,
                        user: {
                          id: cuid(),
                          birthDate: null,
                        },
                      },
                    });
                  }}
                >
                  <FaPlus size={14} />
                  <span>Ajouter mon conjoint(e)</span>
                </Button>
              )}
              <FieldArray
                name={"children"}
                render={(arrayHelpers) => [
                  get(values, "children", []).map((_, i) => (
                    <RelatedPerson
                      key={`RelatedPerson-${i}`}
                      products={products}
                      title={`${TRANSLATION.NUMBERS_MAPPING[i + 1] || i + 1} ${
                        TRANSLATION.CHILDREN_BIRTHDATES.CHILD
                      }`}
                      contact={get(values, `children.${i}.identity`)}
                      TRANSLATION={TRANSLATION}
                      selectedProducts={selectedProducts}
                      setSelectedProducts={setSelectedProducts}
                      disabledDate={(current) => {
                        return (
                          current > dayjs() ||
                          current < twentySevenYearsAgoStart
                        );
                      }}
                      defaultPickerValue={
                        !get(values, `children.${i}.identity.user.birthDate`) &&
                        dayjs().subtract(1, "years")
                      }
                      inputProps={{
                        ...bindDateInputProps({
                          name: `children.${i}.identity.user.birthDate`,
                          values,
                          ...formProps,
                        }),
                      }}
                      onDelete={() => {
                        const key = get(values, `children.${i}.identity.id`);
                        setSelectedProducts((prev) => {
                          const updated = { ...prev };
                          delete updated[key];
                          return updated;
                        });
                        arrayHelpers.remove(i);
                      }}
                    />
                  )),
                  get(values, "children", []).length < 12 && (
                    <Button
                      key="add-child"
                      type={"primary-light"}
                      className="children__count__button"
                      onClick={() => {
                        const id = cuid();
                        setSelectedProducts((prev) => ({
                          ...prev,
                          [id]: undefined,
                        }));
                        arrayHelpers.push({
                          id: cuid(),
                          relationship: RELATIONSHIP.CHILD,
                          identity: {
                            id,
                            user: {
                              id: cuid(),
                            },
                          },
                        });
                      }}
                    >
                      <FaPlus size={14} />
                      <span>Ajouter un enfant</span>
                    </Button>
                  ),
                ]}
              />
            </div>
          </div>
          <ActionButtons
            disabled={!isEmpty(formProps.errors) || !allDefined}
            onNext={handleSubmit}
            onBack={onBack}
            buttonText="Valider et continuer"
          />
          <Drawer
            placement="right"
            className="amellis__drawer right--drawer"
            closable
            onClose={() => setDrawerGuaranteeVisible(false)}
            open={drawerGuaranteeVisible}
            width={isMobile ? "98%" : "50%"}
          >
            {selectedGuarantee && (
              <div className="guarantees__wrapper">
                <div className="guarantees__wrapper--header">
                  <div className="title">
                    <span>La garantie phare</span>
                    <h3>{selectedGuarantee.mainInfo}</h3>
                  </div>
                  <p>{selectedGuarantee.description}</p>
                </div>

                <div className="guarantees__wrapper--content">
                  <span>Exemple de remboursement</span>
                  <h4>{selectedGuarantee.example}</h4>

                  <div className="bars">
                    {(() => {
                      const ss = parseFloat(
                        selectedGuarantee.details?.socialSecurity ?? 0,
                      );
                      const ins = parseFloat(
                        selectedGuarantee.details?.insurance ?? 0,
                      );
                      const rem = parseFloat(
                        selectedGuarantee.details?.remaining ?? 0,
                      );
                      const total = ss + ins + rem;

                      const getWidth = (value) => {
                        const percent = total ? (value / total) * 100 : 0;
                        return `${Math.max(percent, 10)}%`;
                      };

                      return (
                        <>
                          <div className="item social">
                            <div className="head">
                              <label>Sécurité sociale</label>
                              <span className="value">{ss.toFixed(2)} €</span>
                            </div>
                            <span
                              className="bar"
                              style={{ "--progress": getWidth(ss) }}
                            ></span>
                          </div>
                          <div className="item insurance">
                            <div className="head">
                              <label>Amellis</label>
                              <span className="value">{ins.toFixed(2)} €</span>
                            </div>
                            <span
                              className="bar"
                              style={{ "--progress": getWidth(ins) }}
                            ></span>
                          </div>
                          <div className="item remaining">
                            <div className="head">
                              <label>Reste à charge</label>
                              <span className="value">{rem.toFixed(2)} €</span>
                            </div>
                            <span
                              className="bar"
                              style={{ "--progress": getWidth(rem) }}
                            ></span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </Drawer>
        </Layout>,
      ]}
    </Formik>
  );
};

export default View;
